import type { ProjectDetail as Project } from "@esi/shared";
import { Upload } from "lucide-react";
import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import type { ConversationScope } from "../conversation/artefacts/queries.ts";
import { KnowledgeTree, useUploadDialog } from "./KnowledgeTree.tsx";

/**
 * Beside a conversation, everything it can draw on, as one tree (spec `web-shell` "Knowledge
 * tree sections"): Wiki and Sources (files) with their folders, Connected once a connector
 * exists, then the conversation's Artefacts once it has one. The lists come from the server
 * already filtered by the viewer's access; the tree only organises them. `narrow` (the sheet)
 * opens folders as the tree's root instead of expanding them.
 */
export function KnowledgePanel({
  project,
  narrow,
  conversation,
  onOpenArtefact,
}: {
  project: Project;
  narrow: boolean;
  /** The open conversation, whose artefacts end the tree (`artefacts-viewer`). */
  conversation?: ConversationScope | null;
  onOpenArtefact?: (artefactId: string, version: number) => void;
}) {
  const { t } = useTranslation();
  // Unique per panel: the sheet's closing panel and the docked one can overlap for a moment.
  const headingId = useId();
  const upload = useUploadDialog(project);
  // In the sheet, an upload starts in the folder opened as the tree's root.
  const [root, setRoot] = useState<string | null>(null);
  const uploadButton = upload.upload ? (
    <Button
      size="sm"
      variant="ghost"
      disabled={!upload.ready}
      onClick={() => upload.upload?.open(narrow ? root : null)}
    >
      <Upload aria-hidden="true" strokeWidth={1.75} />
      {t("files.upload")}
    </Button>
  ) : null;

  return (
    <section
      data-esi-content="files"
      aria-labelledby={headingId}
      className="flex min-h-0 flex-1 flex-col"
    >
      {narrow ? (
        // The sheet's own header already says "Knowledge": no second bar, Upload by the search.
        <h2 id={headingId} className="sr-only">
          {t("knowledgePanel.title")}
        </h2>
      ) : (
        // Same bar height as the conversation header, so both bottom hairlines meet.
        <div className="flex h-bar shrink-0 items-center justify-between gap-3 border-b border-line pr-2 pl-4">
          <h2 id={headingId} className="text-sm font-semibold text-fg">
            {t("knowledgePanel.title")}
          </h2>
          {uploadButton}
        </div>
      )}
      <KnowledgeTree
        project={project}
        scope="all"
        narrow={narrow}
        upload={upload.upload}
        toolbar={narrow ? uploadButton : undefined}
        onRootChange={setRoot}
        conversation={conversation}
        onOpenArtefact={onOpenArtefact}
      />
      {upload.dialog}
    </section>
  );
}
